/**
 * TicketsCollection — FBA-styled Featured Collection wrapper that houses
 * two compact sub-rails: Concerts + Clean Comedy. Mirrors the exact
 * chrome of the Foundational Collection on Home (gradient bg, blur
 * orbs, "Featured collection" small-caps amber label, big title +
 * description, two sub-rails with amber tiny-caps headers + "See all").
 *
 * Pulls live Ticketmaster data via /api/concerts/near + /api/comedy/near
 * after silently detecting the user's state via /api/geo/detect-state.
 * Renders nothing while loading or if both rails are empty + state==null
 * (so the box never appears as a bare frame).
 */

import React, { useEffect, useState } from "react";
import { Ticket, MicrophoneStage, CalendarBlank } from "@phosphor-icons/react";
import axios from "axios";

const STATE_KEY = "hw-concert-city";

// 50 US states (same list the Concerts / Comedy rails use). Drives
// the inline native <select> picker that opens when the user taps
// "Check all states" — same grey OS dropdown the founder asked for.
const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

function readSavedState() {
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o && o.state) return o;
  } catch (_) { /* noop */ }
  return null;
}

function shortDate(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "TBA";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch (_) { return "TBA"; }
}

function MiniCard({ ev, color }) {
  const initials = (ev.artist || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const accent = color === "amber" ? "text-amber-200" : "text-emerald-200";
  return (
    <a
      href={ev.ticket_url}
      target="_blank"
      rel="noopener noreferrer"
      className="group shrink-0 w-36 md:w-40 text-left"
      title={ev.artist}
      data-testid={`tickets-card-${ev.id}`}
    >
      <div className="aspect-video relative rounded-xl overflow-hidden bg-forest-700">
        {ev.image_url ? (
          <img src={ev.image_url} alt={ev.artist} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-cream/80 font-bold text-xl">{initials}</div>
        )}
        <div className="absolute inset-0 bg-black/35 group-hover:bg-black/15 transition-colors" />
        <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 rounded-full bg-black/60 backdrop-blur px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-cream font-semibold">
          <CalendarBlank size={9} weight="bold" />
          {shortDate(ev.start)}
        </div>
      </div>
      <div className="text-[11px] mt-1.5 text-cream truncate font-semibold">{ev.artist}</div>
      <div className={`text-[10px] truncate ${accent}`}>{ev.city ? `${ev.city}, ${ev.state}` : "Get tickets →"}</div>
    </a>
  );
}

export default function TicketsCollection() {
  // `state` is the active 2-letter code (e.g. "CA"). Sources, in order:
  //   1. Manual pick saved in localStorage (manual: true) — sticks
  //   2. Fresh geo-IP detection on every mount when no manual override
  //      exists, so subscribers always see tickets in the state they're
  //      physically in (same pattern as the devotional re-checking time)
  //   3. "ALL" fallback when geo fails
  const saved = (typeof window !== "undefined") ? readSavedState() : null;
  const initialState = (saved && saved.manual && saved.state) ? saved.state : null;
  const [state, setState] = useState(initialState);
  const [concerts, setConcerts] = useState([]);
  const [comedy, setComedy] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  // Geo-detect on mount only when no manual pick is active.
  useEffect(() => {
    if (initialState) return;
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/geo/detect-state`)
      .then((r) => {
        if (cancelled) return;
        const s = r?.data?.state;
        setState(s || "ALL");
      })
      .catch(() => { if (!cancelled) setState("ALL"); });
    return () => { cancelled = true; };
  }, []);

  // Clear any saved override + force-rerun geo detection. Used by the
  // "📍 Use my location" picker option to recover from a stale manual
  // pick made earlier on a different device/state.
  const handleUseMyLocation = () => {
    try { window.localStorage.removeItem(STATE_KEY); } catch (_) { /* noop */ }
    setShowPicker(false);
    setLoaded(false);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/geo/detect-state`)
      .then((r) => {
        const s = r?.data?.state;
        setState(s || "ALL");
      })
      .catch(() => setState("ALL"));
  };

  // Fetch both feeds in parallel once we have a state.
  useEffect(() => {
    if (!state) return;
    let cancelled = false;
    Promise.all([
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/concerts/near`, { params: { state } }).catch(() => null),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/comedy/near`, { params: { state } }).catch(() => null),
    ]).then(([c, k]) => {
      if (cancelled) return;
      const c_events = (c?.data?.events || []).slice(0, 12);
      const k_events = (k?.data?.events || []).slice(0, 12);
      setConcerts(c_events);
      setComedy(k_events);
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [state]);

  // Hide entire collection if both rails are empty (e.g. WY) so the
  // gradient frame never appears empty. Loading state also hides it.
  if (!loaded || (!concerts.length && !comedy.length)) return null;

  const label = state === "ALL" ? "All U.S." : state;

  return (
    <section className="mt-6 md:mt-8" data-testid="home-tickets-collection">
      <div
        className="relative overflow-hidden rounded-2xl p-3 md:p-4 text-cream shadow-md"
        style={{ background: "linear-gradient(135deg, #0c1f1a 0%, #14463a 45%, #1f8c6e 105%)" }}
      >
        <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-cream/5 blur-3xl" />
        <div className="absolute -left-8 -bottom-12 w-28 h-28 rounded-full bg-amber-400/15 blur-3xl" />
        <div className="relative">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <span className="text-[9px] tracking-[0.25em] uppercase font-semibold text-amber-200">Featured collection</span>
              <h2 className="font-heading text-base md:text-lg mt-0.5">Live Tickets · {label}</h2>
              <p className="text-cream/80 text-[11px] mt-0.5 max-w-xl">
                Christian, gospel, worship and clean-comedy tour dates — powered by Ticketmaster.
              </p>
            </div>
            {/* "Check all states" toggles a native <select> with all
                50 U.S. states + an "All states" option — same OS-level
                grey picker the Concerts/Comedy rails use. Tapping a
                state writes to localStorage and refetches both feeds. */}
            {showPicker ? (
              <select
                value=""
                onChange={(e) => {
                  const s = (e.target.value || "").trim();
                  setShowPicker(false);
                  if (!s) return;
                  if (s === "__cancel__") return;
                  if (s === "__locate__") { handleUseMyLocation(); return; }
                  const code = s.toUpperCase();
                  // Mark as a deliberate manual pick so the next mount
                  // respects it instead of re-running geo detect.
                  try { window.localStorage.setItem(STATE_KEY, JSON.stringify({ city: "", state: code, manual: true })); } catch (_) { /* noop */ }
                  setState(code);
                }}
                onBlur={() => setShowPicker(false)}
                className="shrink-0 rounded-full bg-amber-300 text-ink px-3 py-1 text-[11px] font-bold uppercase tracking-wider cursor-pointer focus:outline-none"
                data-testid="tickets-state-picker"
                autoFocus
              >
                <option value="" disabled>Pick a state…</option>
                <option value="__locate__">📍 Use my location</option>
                <option value="ALL">🇺🇸 All states</option>
                <option disabled>──────────</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>{code} — {name}</option>
                ))}
                <option disabled>──────────</option>
                <option value="__cancel__">Cancel</option>
              </select>
            ) : (
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="shrink-0 rounded-full bg-amber-300 text-ink px-3 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-amber-200 transition"
                data-testid="tickets-check-all-states"
              >
                🇺🇸 Change state
              </button>
            )}
          </div>

          {/* Concerts sub-rail */}
          {concerts.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-amber-200 inline-flex items-center gap-1">
                  <Ticket size={10} weight="duotone" /> Concerts · Gospel · Worship · Faith Based
                </h3>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {concerts.map((ev) => <MiniCard key={ev.id} ev={ev} color="emerald" />)}
              </div>
            </div>
          )}

          {/* Comedy sub-rail */}
          {comedy.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-amber-200 inline-flex items-center gap-1">
                  <MicrophoneStage size={10} weight="duotone" /> Clean Comedy · Family-friendly
                </h3>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {comedy.map((ev) => <MiniCard key={ev.id} ev={ev} color="amber" />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
